import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Truck, Calendar, Tag, Radio, Trash2, Loader2, Sparkles, MapPin, Crosshair, StopCircle } from "lucide-react";
import { toast } from "sonner";
import BusinessLocationPingCard from "@/components/BusinessLocationPingCard";

/**
 * MobileManage — one-stop dashboard for a food truck / pop-up owner.
 *
 * Sections:
 *   1. Setup — mobile type + cuisine + existing check-in composer (BusinessLocationPingCard)
 *   2. Right-now controls — Go Live, End check-in, Boost (marketing add-on)
 *   3. Scheduled stops — CRUD
 *   4. Promo codes — CRUD (marketing add-on)
 */
export default function MobileManage() {
  const { user, refresh } = useAuth();
  const [savedType, setSavedType] = useState(user?.mobile_type || "food_truck");
  const [cuisine, setCuisine] = useState(user?.cuisine || "");
  const [savingType, setSavingType] = useState(false);
  const [subActive, setSubActive] = useState(false);
  const [mktActive, setMktActive] = useState(false);

  useEffect(() => {
    api.get("/me").then(({ data }) => {
      setSubActive(!!data.user?.subscription_active);
      setMktActive(!!data.user?.marketing_active);
    }).catch(() => {});
  }, []);

  const saveType = async () => {
    setSavingType(true);
    try {
      await api.put("/me/business", { is_mobile: true, mobile_type: savedType, cuisine });
      toast.success("Mobile profile updated");
      if (refresh) refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingType(false);
    }
  };

  if (!user) {
    return <div className="max-w-md mx-auto py-20 text-center text-gray-500">Sign in to manage your mobile business.</div>;
  }
  if (user.role !== "business") {
    return (
      <div className="max-w-md mx-auto py-20 text-center">
        <Truck size={30} className="mx-auto text-emerald-700 mb-3" />
        <h1 className="font-display text-2xl">Business accounts only</h1>
        <p className="text-sm text-gray-500 mt-2">Food trucks and pop-ups sign up as businesses.</p>
        <Link to="/list-your-business"><Button className="mt-4 rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white">List your business (free)</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-10 space-y-6" data-testid="mobile-manage-page">
      <header>
        <div className="overline text-emerald-700 flex items-center gap-2"><Truck size={12} /> Mobile business</div>
        <h1 className="font-display text-4xl tracking-tighter mt-1">Manage check-ins & stops</h1>
        <p className="mt-2 text-gray-600 max-w-2xl">
          Set your GPS-tagged live spot, schedule where you'll be next, and hand out limited-time promo codes.
          Boost your pin to the top of the <Link to="/foodtrucks" className="text-[#B31E1E] hover:underline">/foodtrucks</Link> map with the Marketing add-on.
        </p>
      </header>

      {/* 1. Setup */}
      <Card className="rounded-md p-6" data-testid="mobile-setup-card">
        <h2 className="font-display text-xl">Mobile profile</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <Label className="text-xs font-mono uppercase tracking-wider text-gray-600">Type</Label>
            <select
              value={savedType}
              onChange={(e) => setSavedType(e.target.value)}
              className="rounded-sm border border-border bg-white text-sm px-3 py-2 w-full mt-1"
              data-testid="mobile-type-select"
            >
              <option value="food_truck">Food truck</option>
              <option value="popup">Pop-up shop</option>
              <option value="mobile_service">Mobile service</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-wider text-gray-600">Cuisine / specialty</Label>
            <Input value={cuisine} onChange={(e) => setCuisine(e.target.value)} placeholder="Tacos, BBQ, vintage clothing…" className="rounded-sm mt-1" data-testid="mobile-cuisine-input" />
          </div>
        </div>
        <Button onClick={saveType} disabled={savingType} className="mt-4 rounded-[35px] bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="mobile-type-save">
          {savingType ? (<><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving…</>) : "Save mobile profile"}
        </Button>
      </Card>

      {/* 2. Existing ping composer — GPS check-in + email broadcast */}
      <BusinessLocationPingCard marketingActive={mktActive} subscriptionActive={subActive} />

      {/* 3. Right-now controls */}
      <RightNowControls user={user} refresh={refresh} />

      {/* 4. Scheduled stops */}
      <ScheduledStops />

      {/* 5. Promo codes */}
      <PromoCodes marketingActive={mktActive} subscriptionActive={subActive} />
    </div>
  );
}

function RightNowControls({ user, refresh }) {
  const [busy, setBusy] = useState("");
  const loc = user?.current_location;
  const isLive = !!(loc?.pinged_at && !loc?.ended_at);

  const endPing = async () => {
    setBusy("end");
    try {
      await api.post("/me/business/location-ping/end");
      toast.success("Check-in ended");
      if (refresh) refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };
  const boost = async () => {
    setBusy("boost");
    try {
      await api.post("/me/business/location-ping/boost");
      toast.success("Your pin is now boosted — it'll show at the top for the next 6 hours");
      if (refresh) refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <Card className="rounded-md p-6" data-testid="mobile-controls-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl">Right now</h2>
          {isLive ? (
            <p className="text-xs text-emerald-700 mt-1 inline-flex items-center gap-1.5"><MapPin size={11} /> Checked in — {loc.label} {loc.boosted && <Badge className="rounded-sm text-[9px] bg-amber-500 text-white ml-1"><Sparkles size={8} className="mr-0.5" /> Boosted</Badge>}</p>
          ) : (
            <p className="text-xs text-gray-500 mt-1">You're not checked in. Ping your location above to appear on the /foodtrucks map.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/marketplace/live/new?checkin=1`}>
            <Button size="sm" className="rounded-[35px] bg-red-600 hover:bg-red-700 text-white" data-testid="mobile-go-live">
              <Radio size={13} className="mr-1.5" /> Go live from here
            </Button>
          </Link>
          <Button size="sm" onClick={boost} disabled={!isLive || loc?.boosted || busy === "boost"} className="rounded-[35px] bg-amber-500 hover:bg-amber-600 text-white" data-testid="mobile-boost">
            {busy === "boost" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <Sparkles size={13} className="mr-1.5" />} Boost pin
          </Button>
          <Button size="sm" variant="outline" onClick={endPing} disabled={!isLive || busy === "end"} className="rounded-[35px]" data-testid="mobile-end-checkin">
            {busy === "end" ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : <StopCircle size={13} className="mr-1.5" />} End check-in
          </Button>
        </div>
      </div>
    </Card>
  );
}

function ScheduledStops() {
  const [stops, setStops] = useState([]);
  const [title, setTitle] = useState("");
  const [addr, setAddr] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [busy, setBusy] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/business/mobile-stops");
      setStops(data?.items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const detect = () => {
    if (!("geolocation" in navigator)) return;
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setCoords({ lat: p.coords.latitude, lng: p.coords.longitude }); setDetecting(false); toast.success("GPS locked"); },
      (err) => { toast.error(`Location denied: ${err.message}`); setDetecting(false); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  const add = async (e) => {
    e.preventDefault();
    if (title.trim().length < 2 || !startsAt) {
      toast.error("Title and start time are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/business/mobile-stops", {
        title: title.trim(),
        address_label: addr.trim(),
        lat: coords.lat, lng: coords.lng,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      });
      toast.success("Stop scheduled");
      setTitle(""); setAddr(""); setStartsAt(""); setEndsAt(""); setCoords({ lat: null, lng: null });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this scheduled stop?")) return;
    try {
      await api.delete(`/me/business/mobile-stops/${id}`);
      setStops((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="rounded-md p-6" data-testid="mobile-stops-card">
      <h2 className="font-display text-xl flex items-center gap-2"><Calendar size={18} /> Scheduled stops</h2>
      <p className="text-xs text-gray-500 mt-1">Tell regulars where you'll be. Upcoming stops appear on your business profile.</p>

      <form onSubmit={add} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Downtown farmer's market" className="rounded-sm" data-testid="stop-title" />
        <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Address / cross-street" className="rounded-sm" data-testid="stop-addr" />
        <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="rounded-sm font-mono" data-testid="stop-starts" />
        <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} className="rounded-sm font-mono" data-testid="stop-ends" />
        <div className="md:col-span-2 flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={detect} disabled={detecting} className="rounded-[35px]" data-testid="stop-gps">
            {detecting ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Crosshair size={12} className="mr-1.5" />} GPS pin
          </Button>
          {coords.lat != null && <span className="text-[11px] font-mono text-emerald-700">{coords.lat.toFixed(4)}, {coords.lng.toFixed(4)}</span>}
          <Button type="submit" disabled={busy} className="ml-auto rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="stop-add">
            {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : "Add stop"}
          </Button>
        </div>
      </form>

      {stops.length > 0 && (
        <ul className="mt-5 divide-y divide-border" data-testid="stops-list">
          {stops.map((s) => (
            <li key={s.id} className="py-3 flex items-center gap-3" data-testid={`stop-${s.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{s.title}</div>
                <div className="text-[11px] text-gray-500 truncate">
                  {new Date(s.starts_at).toLocaleString()}
                  {s.ends_at ? ` – ${new Date(s.ends_at).toLocaleString()}` : ""}
                  {s.address_label ? ` · ${s.address_label}` : ""}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => remove(s.id)} className="rounded-sm text-red-600" data-testid={`stop-delete-${s.id}`}><Trash2 size={13} /></Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function PromoCodes({ marketingActive, subscriptionActive }) {
  const gated = !(marketingActive && subscriptionActive);
  const [promos, setPromos] = useState([]);
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [pct, setPct] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/business/mobile-promos");
      setPromos(data?.items || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (!gated) load(); }, [gated]);

  const add = async (e) => {
    e.preventDefault();
    if (code.trim().length < 3 || desc.trim().length < 3) {
      toast.error("Code and description are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        code: code.trim().toUpperCase(),
        description: desc.trim(),
        discount_pct: pct ? Math.max(1, Math.min(90, parseInt(pct, 10))) : null,
      };
      if (expires) payload.expires_at = new Date(expires).toISOString();
      await api.post("/me/business/mobile-promos", payload);
      toast.success("Promo code created");
      setCode(""); setDesc(""); setPct(""); setExpires("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this promo code?")) return;
    try {
      await api.delete(`/me/business/mobile-promos/${id}`);
      setPromos((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Card className="rounded-md p-6" data-testid="mobile-promos-card">
      <h2 className="font-display text-xl flex items-center gap-2"><Tag size={18} /> Limited-time promo codes</h2>
      <p className="text-xs text-gray-500 mt-1">Hand out codes tied to your check-in. Customers see them on your live pin.</p>

      {gated ? (
        <div className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
          Promo codes require an active Servrevu Pro subscription + Marketing add-on ($35/mo). <Link to="/pricing" className="underline">Upgrade →</Link>
        </div>
      ) : (
        <>
          <form onSubmit={add} className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="TACO20" className="rounded-sm font-mono uppercase" data-testid="promo-code" />
            <Input value={pct} onChange={(e) => setPct(e.target.value)} placeholder="20" type="number" min="1" max="90" className="rounded-sm" data-testid="promo-pct" />
            <Input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} className="rounded-sm font-mono" data-testid="promo-expires" />
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="20% off tacos — today only, mention code at the truck" className="rounded-sm md:col-span-4" rows={2} data-testid="promo-desc" />
            <div className="md:col-span-4 flex items-center gap-2">
              <p className="text-[11px] text-gray-500 flex-1">Leave expiry blank for 24-hour auto-expiry.</p>
              <Button type="submit" disabled={busy} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="promo-add">
                {busy ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : "Add promo"}
              </Button>
            </div>
          </form>

          {promos.length > 0 && (
            <ul className="mt-5 divide-y divide-border" data-testid="promos-list">
              {promos.map((p) => (
                <li key={p.id} className="py-3 flex items-center gap-3" data-testid={`promo-${p.id}`}>
                  <Badge className="rounded-sm bg-emerald-700 font-mono">{p.code}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{p.description}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {p.discount_pct ? `${p.discount_pct}% off` : p.discount_cents ? `$${(p.discount_cents / 100).toFixed(2)} off` : ""}
                      {p.expires_at ? ` · expires ${new Date(p.expires_at).toLocaleString()}` : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => remove(p.id)} className="rounded-sm text-red-600" data-testid={`promo-delete-${p.id}`}><Trash2 size={13} /></Button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
